import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AppHeader } from "@/components/AppHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Phone, Clock, Copy, UserPlus, MessageSquare, Calendar, Play, Pause, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Messages = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [messages, setMessages] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioProgress, setAudioProgress] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [audioCurrentTime, setAudioCurrentTime] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showAdvanced, setShowAdvanced] = useState(false);  // Afficher version avancée
  
  useEffect(() => {
    const loadMessages = async () => {
      setLoading(true);
      const user = JSON.parse(localStorage.getItem('fruitycall_user') || '{}');
      const userEmail = user.email;
      
      if (!userEmail) {
        setLoading(false);
        return;
      }
      
      try {
        const response = await fetch(`${API_URL}/api/voice/messages?user_email=${encodeURIComponent(userEmail)}&limit=50`);
        const data = await response.json();
        
        if (data.messages && data.messages.length > 0) {
          // Transformer les messages de l'API au format attendu
          const apiMessages = data.messages.map(msg => ({
            id: msg._id,
            caller: msg.caller_phone || "Numéro inconnu",
            callerName: msg.caller_name || extractNameFromNumber(msg.caller_phone),
            date: new Date(msg.created_at),
            duration: formatDuration(msg.recording_duration),
            isNew: msg.is_new,
            preview: msg.transcription ? msg.transcription.substring(0, 80) + "..." : "Transcription en cours...",
            transcription: msg.transcription || "Transcription en attente...",
            rawEmailContent: msg.raw_email_content || null,  // Contenu brut de l'email
            audioUrl: msg.recording_url ? `${process.env.REACT_APP_BACKEND_URL}${msg.recording_url}` : null
          }));
          
          setMessages(apiMessages);
          
          if (id) {
            const msg = apiMessages.find(m => m.id === id);
            setSelectedMessage(msg || apiMessages[0]);
          } else {
            setSelectedMessage(apiMessages[0]);
          }
        } else {
          // Fallback aux messages mockés si pas de messages réels
          loadMockMessages();
        }
      } catch (error) {
        console.error("Erreur chargement messages:", error);
        loadMockMessages();
      } finally {
        setLoading(false);
      }
    };
    
    const loadMockMessages = () => {
      const mockMessages = [
        {
          id: "1",
          caller: "06 12 34 56 78",
          callerName: "Mme Leblanc",
          date: new Date(Date.now() - 2 * 60 * 60 * 1000),
          duration: "1:23",
          isNew: true,
          preview: "Bonjour docteur, je souhaiterais prendre rendez-vous pour un contrôle de routine...",
          transcription: "Bonjour docteur, c'est Madame Leblanc à l'appareil. Je souhaiterais prendre rendez-vous pour un contrôle de routine. J'ai des disponibilités en fin de semaine, jeudi ou vendredi après-midi si possible. J'ai également besoin de renouveler mon ordonnance pour mon traitement habituel. Pouvez-vous me rappeler quand vous aurez un moment ? Merci beaucoup. Bonne journée.",
          audioUrl: null
        },
        {
          id: "2",
          caller: "06 98 76 54 32",
          callerName: "Mme Martin",
          date: new Date(Date.now() - 5 * 60 * 60 * 1000),
          duration: "0:45",
          isNew: true,
          preview: "C'est Madame Martin, je vous appelle pour renouveler mon ordonnance habituelle...",
          transcription: "Bonjour, c'est Madame Martin. Je vous appelle pour renouveler mon ordonnance habituelle pour mon traitement de l'hypertension. Je n'ai plus que pour quelques jours. Est-il possible de passer la chercher demain à votre cabinet ? Merci d'avance.",
          audioUrl: null
        }
      ];
      setMessages(mockMessages);
      setSelectedMessage(mockMessages[0]);
    };
    
    const extractNameFromNumber = (phone) => {
      if (!phone) return "Inconnu";
      // Pour l'instant, utiliser le numéro comme nom
      return phone;
    };
    
    const formatDuration = (seconds) => {
      if (!seconds) return "-";
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    };
    
    loadMessages();
  }, [id]);
  
  const formatDate = (date) => {
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) {
      const diffInMinutes = Math.floor((now - date) / (1000 * 60));
      return `Il y a ${diffInMinutes} min`;
    } else if (diffInHours < 24) {
      return `Il y a ${diffInHours}h`;
    } else if (diffInHours < 48) {
      return "Hier";
    } else {
      return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
    }
  };
  
  const formatFullDate = (date) => {
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const filteredMessages = messages.filter(
    msg => 
      msg.caller.includes(searchQuery) || 
      msg.preview.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (msg.callerName && msg.callerName.toLowerCase().includes(searchQuery.toLowerCase()))
  );
  
  const handleSelectMessage = async (message) => {
    setSelectedMessage(message);
    setShowAdvanced(false);  // Réinitialiser la vue avancée
    // Mark as read
    if (message.isNew) {
      setMessages(prev => prev.map(m => 
        m.id === message.id ? { ...m, isNew: false } : m
      ));
      
      // Appeler l'API pour marquer comme lu
      try {
        const user = JSON.parse(localStorage.getItem('fruitycall_user') || '{}');
        await fetch(`${API_URL}/api/voice/messages/${message.id}/read?user_email=${encodeURIComponent(user.email)}`, {
          method: 'PATCH'
        });
      } catch (error) {
        console.error("Erreur marquage lu:", error);
      }
    }
  };
  
  const handleCopyMessage = () => {
    if (selectedMessage) {
      navigator.clipboard.writeText(selectedMessage.transcription);
      toast.success("Message copié dans le presse-papiers");
    }
  };
  
  const handleCopyPhone = () => {
    if (selectedMessage) {
      navigator.clipboard.writeText(selectedMessage.caller);
      toast.success("Numéro de téléphone copié");
    }
  };
  
  const handleAddContact = () => {
    toast.info("Fonctionnalité : Ajouter aux contacts (à implémenter)");
  };
  
  const handleSendSMS = () => {
    if (selectedMessage) {
      window.open(`sms:${selectedMessage.caller.replace(/\s/g, '')}`);
    }
  };
  
  const handleDoctolib = () => {
    window.open("https://www.doctolib.fr", "_blank");
    toast.success("Redirection vers Doctolib");
  };
  
  const togglePlayback = () => {
    const audio = document.getElementById("audio-player");
    if (audio) {
      if (isPlaying) { audio.pause(); } else { audio.play(); }
    }
    setIsPlaying(!isPlaying);
    
  };
  
  const newCount = messages.filter(msg => msg.isNew).length;
  
  return (
    <div className="flex min-h-screen flex-col bg-background">
      <AppHeader />
      
      <main className="flex flex-1 overflow-hidden">
        {/* Left Panel - Messages List */}
        <div className="w-full md:w-96 border-r bg-card flex flex-col">
          {/* Header */}
          <div className="p-4 border-b">
            <h2 className="text-lg font-semibold mb-1">Messages</h2>
            <p className="text-sm text-muted-foreground">
              {newCount > 0 ? (
                <span className="text-accent font-medium">{newCount} nouveau{newCount > 1 ? 'x' : ''}</span>
              ) : (
                "Aucun nouveau message"
              )}
            </p>
          </div>
          
          {/* Search */}
          <div className="p-4 border-b">
            <Input
              type="search"
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full"
            />
          </div>
          
          {/* Messages List */}
          <ScrollArea className="flex-1">
            <div className="p-2">
              {filteredMessages.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <Phone className="mb-2 h-8 w-8 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">Aucun message</p>
                </div>
              ) : (
                filteredMessages.map((message) => (
                  <button
                    key={message.id}
                    onClick={() => handleSelectMessage(message)}
                    className={`w-full text-left p-3 rounded-lg transition-colors mb-1 ${
                      selectedMessage?.id === message.id
                        ? 'bg-primary/10 border-l-4 border-primary'
                        : 'hover:bg-muted border-l-4 border-transparent'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium truncate">
                          {message.callerName || message.caller}
                        </span>
                        {message.isNew && (
                          <Badge className="bg-accent/20 text-accent-foreground text-xs px-1.5 py-0">
                            New
                          </Badge>
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground shrink-0">
                        {formatDate(message.date)}
                      </span>
                    </div>
                    
                    <p className="text-sm text-muted-foreground line-clamp-2 mb-1">
                      {message.preview}
                    </p>
                    
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      <span>{message.duration}</span>
                    </div>
                  </button>
                ))
              )}
            </div>
          </ScrollArea>
        </div>
        
        {/* Right Panel - Message Detail */}
        <div className="flex-1 flex flex-col bg-background">
          {selectedMessage ? (
            <>
              {/* Message Header */}
              <div className="p-6 border-b bg-card">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h2 className="text-xl font-semibold mb-1">
                      {selectedMessage.callerName || "Appelant inconnu"}
                    </h2>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Phone className="h-4 w-4" />
                      <span>{selectedMessage.caller}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                      <Clock className="h-4 w-4" />
                      <span>{formatFullDate(selectedMessage.date)}</span>
                      <span>•</span>
                      <span>Durée : {selectedMessage.duration}</span>
                    </div>
                  </div>
                  
                  {selectedMessage.isNew && (
                    <Badge className="bg-accent/20 text-accent-foreground">
                      Nouveau
                    </Badge>
                  )}
                </div>
                
                {/* Action Buttons */}
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCopyMessage}
                    className="gap-2"
                  >
                    <Copy className="h-4 w-4" />
                    Copier le message
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCopyPhone}
                    className="gap-2"
                  >
                    <Phone className="h-4 w-4" />
                    Copier le numéro
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleAddContact}
                    className="gap-2"
                  >
                    <UserPlus className="h-4 w-4" />
                    Ajouter aux contacts
                  </Button>
                </div>
              </div>
              
              {/* Audio Player (if available) */}
              {selectedMessage.audioUrl && (
                <div className="p-4 border-b bg-muted/30">
                  <audio
                    id="audio-player"
                    src={selectedMessage.audioUrl}
                    onEnded={() => { setIsPlaying(false); setAudioProgress(0); setAudioCurrentTime(0); }}
                    onTimeUpdate={(e) => {
                      const audio = e.currentTarget;
                      setAudioCurrentTime(audio.currentTime);
                      setAudioProgress(audio.duration ? (audio.currentTime / audio.duration) * 100 : 0);
                    }}
                    onLoadedMetadata={(e) => setAudioDuration(e.currentTarget.duration)}
                  />
                  <div className="flex items-center gap-4">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={togglePlayback}
                      className="gap-2"
                    >
                      {isPlaying ? (
                        <>
                          <Pause className="h-4 w-4" />
                          Pause
                        </>
                      ) : (
                        <>
                          <Play className="h-4 w-4" />
                          Écouter
                        </>
                      )}
                    </Button>
                    <span className="text-xs text-muted-foreground w-10">{formatAudioTime(audioCurrentTime)}</span>
                    <div className="flex-1 cursor-pointer" onClick={handleProgressClick}>
                      <div className="h-2 rounded-full bg-muted">
                        <div className="h-full rounded-full bg-primary transition-all" style={{width: audioProgress + "%"}} />
                      </div>
                    </div>
                    <span className="text-xs text-muted-foreground w-10">{formatAudioTime(audioDuration)}</span>
                  </div>
                </div>
              )}
              
              {/* Transcription */}
              <ScrollArea className="flex-1 p-6">
                <div className="max-w-3xl">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-muted-foreground">
                      {showAdvanced ? "EMAIL COMPLET" : "TRANSCRIPTION"}
                    </h3>
                    {selectedMessage.rawEmailContent && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowAdvanced(!showAdvanced)}
                        className="text-xs text-muted-foreground hover:text-foreground"
                      >
                        {showAdvanced ? "Voir transcription" : "Version avancée"}
                      </Button>
                    )}
                  </div>
                  
                  {showAdvanced && selectedMessage.rawEmailContent ? (
                    <div className="bg-muted/30 rounded-lg p-4 border">
                      <p className="text-foreground leading-relaxed whitespace-pre-wrap font-mono text-sm">
                        {selectedMessage.rawEmailContent}
                      </p>
                    </div>
                  ) : (
                    <p className="text-foreground leading-relaxed whitespace-pre-wrap">
                      {selectedMessage.transcription}
                    </p>
                  )}
                </div>
              </ScrollArea>
              
              {/* Response Actions */}
              <div className="p-6 border-t bg-card">
                <h3 className="text-sm font-semibold mb-3">Répondre</h3>
                <div className="flex flex-wrap gap-3">
                  <Button
                    onClick={handleSendSMS}
                    className="gap-2 bg-secondary hover:bg-secondary/90"
                  >
                    <MessageSquare className="h-4 w-4" />
                    Envoyer un SMS
                  </Button>
                  
                  <Button
                    onClick={handleDoctolib}
                    variant="outline"
                    className="gap-2"
                  >
                    <Calendar className="h-4 w-4" />
                    Proposer un RDV Doctolib
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex flex-1 items-center justify-center">
              <div className="text-center">
                <Phone className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
                <p className="text-lg font-medium text-muted-foreground">
                  Sélectionnez un message
                </p>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Messages;